import { useState } from "react";
import { Navigate } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, RemansoFigure } from "@/components/Brand";
import { CrisisNote } from "@/components/CrisisNote";
import { ForgotForm } from "@/components/ForgotForm";
import { ConsentChecks, TermsLink, PrivacyLink } from "@/components/LegalLinks";

const startGoogle = (consented) => {
  if (consented) sessionStorage.setItem("pensive_consent", "1");
  else sessionStorage.removeItem("pensive_consent");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/escribir";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Field = ({ label, id, ...props }) => (
  <label className="block" htmlFor={id}>
    <span className="block text-[15px] font-medium text-tinta mb-2">{label}</span>
    <input id={id} className="field" data-testid={`login-${id}-input`} {...props} />
  </label>
);

const ModeSwitch = ({ mode, setMode }) => (
  <div className="inline-flex rounded-full bg-superficie p-1" role="tablist">
    {[["entrar", "Entrar"], ["crear", "Crear cuenta"]].map(([m, label]) => (
      <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} data-testid={`login-mode-${m}`}
        className={`rounded-full px-4 py-1.5 text-sm transition-colors ${mode === m ? "bg-tinta text-sobre-tinta" : "text-sec hover:text-tinta"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("entrar");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState("");
  const [consent, setConsent] = useState({ terms: false, privacy: false });
  const consented = consent.terms && consent.privacy;

  if (user) return <Navigate to="/escribir" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resendCode = async () => {
    setError("");
    try {
      await api.post("/auth/login", { email: form.email, password: form.password, resend: true });
    } catch (err) {
      setError(err?.response?.status === 428 ? "Te enviamos un código nuevo. Revisa tu correo; puede tardar un minuto." : errorText(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "crear" ? { ...form, accept_terms: consent.terms, accept_privacy: consent.privacy } : { email: form.email, password: form.password, ...(needCode && code ? { code } : {}) };
      const { data } = await api.post(mode === "crear" ? "/auth/register" : "/auth/login", payload);
      setUser(data);
    } catch (err) {
      if (err?.response?.status === 428) setNeedCode(true);
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-papel grid lg:grid-cols-[minmax(0,1fr)_420px]" data-testid="login-page">
      <main className="px-6 py-10 sm:px-12 lg:px-24 lg:py-16 flex flex-col">
        <Logo size={52} testId="login-logo" />
        <div className="max-w-md w-full mt-14 lg:mt-24 settle">
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-tinta">Un lugar para escribir con tus propias palabras.</h1>
          <p className="mt-4 text-sec">Tu diario es solo tuyo. Lo que escribes se guarda cifrado.</p>
          {mode === "olvido" ? <ForgotForm initialEmail={form.email} onBack={() => setMode("entrar")} /> : (<>
          <div className="mt-10"><ModeSwitch mode={mode} setMode={(m) => { setMode(m); setError(""); }} /></div>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            {mode === "crear" && <Field label="Tu nombre" id="name" value={form.name} onChange={set("name")} required autoComplete="name" />}
            <Field label="Correo" id="email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" />
            <Field label="Contraseña" id="password" type="password" value={form.password} onChange={set("password")} required
              minLength={mode === "crear" ? 8 : undefined} autoComplete={mode === "crear" ? "new-password" : "current-password"} />
            {mode === "crear" && <p className="text-xs text-meta">Al menos 8 caracteres.</p>}
            {mode === "crear" && <ConsentChecks value={consent} onChange={setConsent} prefix="register" />}
            {mode === "entrar" && needCode && (
              <Field label="Código de 6 dígitos" id="code" value={code} inputMode="numeric" maxLength={6} autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            )}
            {mode === "entrar" && needCode && (
              <button type="button" className="btn-text -ml-2" onClick={resendCode} data-testid="login-resend-code-button">Enviar otro código</button>
            )}
            {mode === "entrar" && (
              <button type="button" className="btn-text -ml-2" onClick={() => { setMode("olvido"); setError(""); }} data-testid="forgot-password-link">
                Olvidé mi contraseña
              </button>
            )}
            {error && <p className="calm-note" role="status" data-testid="login-error">{error}</p>}
            <button type="submit" className="btn-primary w-full" disabled={busy || (mode === "crear" && !consented)} data-testid="login-submit-button">
              {busy ? "Un momento…" : mode === "crear" ? "Crear cuenta" : "Entrar"}
            </button>
          </form>
          <div className="flex items-center gap-3 my-6 text-xs text-meta"><span className="h-px flex-1 bg-linea" />o<span className="h-px flex-1 bg-linea" /></div>
          <button className="btn-secondary w-full" onClick={() => startGoogle(mode === "crear" && consented)} data-testid="login-google-button">Continuar con Google</button>
          </>)}
          <p className="mt-10 text-[13px] text-sec" data-testid="login-legal-links">
            <TermsLink testId="login-terms-link" /> · <PrivacyLink testId="login-privacy-link">Privacidad</PrivacyLink>
          </p>
        </div>
      </main>
      <aside className="hidden lg:flex flex-col justify-between bg-superficie border-l border-linea px-10 py-16">
        <div>
          <RemansoFigure estado="escucha" height={96} className="breathe" testId="login-remanso-figure" />
          <p className="font-serif text-2xl mt-6">Remanso</p>
          <p className="text-sm text-sec mt-1">Acompaña sin aconsejar</p>
        </div>
        <CrisisNote testId="login-crisis-note" />
      </aside>
      <div className="lg:hidden px-6 pb-10"><CrisisNote testId="login-crisis-note-mobile" /></div>
    </div>
  );
}
